/* global __WEB__ */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import guid from '../service/guid';
import isEqual from 'lodash/isEqual';
import TableSubscriber from '../service/TableSubscriber';

/**
 * This is a hoc that subscribes the wrapped component to a socket
 * if WebSockets are available, otherwise it fetches the data directly.
 * To use the hoc, you'll have to rename your creation and update actions
 * manually in the mapDispatchToProps function like so:
 *
 * const mapDispatchToProps = {
 *     create: myAwesomeCreateAction,
 *     update: myAwesomeUpdateAction,
 * };
 *
 * The required props will also have to be set on the component in order for them
 * to be passed to the wrapped component. This can either be done directly when
 * the component is used, or in a container through the mapStateToProps function.
 *
 * @TODO these quirks should be fixed in a later version, but will probably require
 * some research, because props are named, and it's very difficult to distinguish
 * one property from another.
 */
export default function withRankingSubscription(WrappedComponent) {
    const subTable = class SubscribedTable extends Component {
        static propTypes = {
            // Props
            columns: PropTypes.array.isRequired,
            convert: PropTypes.string,
            leftJoins: PropTypes.string,
            sources: PropTypes.oneOfType([
                PropTypes.array,
                PropTypes.string,
            ]),
            view: PropTypes.string,
            limit: PropTypes.number,
            offset: PropTypes.number,
            filter: PropTypes.string,
            start: PropTypes.string,
            itemSector: PropTypes.string,

            // Dispatchers
            create: PropTypes.func,
            update: PropTypes.func,
            fetch: PropTypes.func,
            dispatch: PropTypes.func.isRequired,
        };

        static defaultProps = {
            // Props
            convert: null,
            filter: null,
            itemSector: null,
            leftJoins: null,
            limit: null,
            offset: null,
            start: null,
            view: null,

            // Dispatchers
            create: null,
            fetch: null,
            update: null,
        };

        componentWillMount() {
            if (__WEB__ && !this.channel) {
                this.tableSubscriber = new TableSubscriber();
            }
        }

        componentDidMount() {
            // If there's no open channel, we have to initialize
            // the TableSubscriber with our dispatchers.
            if (!this.channel) {
                this.tableSubscriber.start(
                    this.props.create,
                    this.props.update,
                    this.props.fetch,
                    this.props.dispatch
                );
            }

            // Be sure to only pass the relevant parameters to the subscriber
            const {
                columns,
                convert,
                leftJoins,
                sources,
                view,
                limit,
                offset,
                filter,
                start,
                itemSector,
            } = this.props;

            // Subscribe to a table with the given parameters and store
            // a reference to the opened channel.
            this.channel = guid();
            this.tableSubscriber.subscribe(this.channel, {
                columns,
                convert,
                leftJoins,
                sources,
                view,
                limit,
                offset,
                filter,
                start,
                itemSector,
            });
        }

        componentWillUnmount() {
            if (this.channel) {
                this.tableSubscriber.unsubscribe(this.channel);
            }
        }

        componentWillReceiveProps(nextProps) {
            // The parameters of the nextProps and the current props
            // have to be built manually, since the symbols also
            // gets added to the props, thus this.props will always
            // be unequal to nextProps when theres data present
            const currentParams = {
                columns: this.props.columns,
                convert: this.props.convert,
                leftJoins: this.props.leftJoins,
                sources: this.props.sources,
                view: this.props.view,
                limit: this.props.limit,
                offset: this.props.offset,
                filter: this.props.filter,
                start: this.props.start,
                itemSector: this.props.itemSector,
            };

            const nextParams = {
                columns: nextProps.columns,
                convert: nextProps.convert,
                leftJoins: nextProps.leftJoins,
                sources: nextProps.sources,
                view: nextProps.view,
                limit: nextProps.limit,
                offset: nextProps.offset,
                filter: nextProps.filter,
                start: nextProps.start,
                itemSector: nextProps.itemSector,
            };

            // Check that the new parameters differ from the current
            // and that we have an open channel.
            // If so, we re-subscribe with the new parameters.
            if (!isEqual(nextParams, currentParams) && this.channel) {
                this.tableSubscriber.unsubscribe(this.channel);
                this.channel = this.tableSubscriber.subscribe(this.channel, nextParams);
            }
        }

        render() {
            return <WrappedComponent {...this.props} />;
        }
    };

    return connect()(subTable);
}



/** WEBPACK FOOTER **
 ** /src/shared/hoc/withTableSubscription.js
 **/